import {
    ReactElement,
    useEffect,
    useState,
} from 'react';

import {
    SimpleGrid,
    Table,
} from '@mantine/core';

import {usePaymentIntents} from '../hooks/usePaymentIntents';
import {PaginationControls} from '../../../components/PaginationControls';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';
import {IPaymentIntentTableColumn, PaymentIntent,} from '../types';
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {getColumns} from '../tables/columns';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/PaymentIntentsService';
import {useQueryClient} from '@tanstack/react-query';

interface RecordsTableProps {
    customerId?: string;
    selectedRecords?: PaymentIntent[];
    setSelectedRecords?: (records: PaymentIntent[]) => void;
    extraTools?: ReactElement[];
    settings?: { label: string, onClick: () => void }[];
}

export const RecordsTable = ({
                                 customerId,
                                 selectedRecords = [],
                                 setSelectedRecords,
                                 extraTools,
                                 settings,
                             }: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions()
    const queryClient = useQueryClient();

    const {
        visibleItems,
        loading,
        fetchRecords,
        handleFilterByKeyword,
        currentPage,
        hasNext,
        hasPrevious,
        goToNextPage,
        goToPreviousPage,
    } = usePaymentIntents(customerId);

    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<PaymentIntent | null>(null);

    useEffect(() => {
        fetchRecords();
    }, []);

    const selection: Record<string, PaymentIntent> = {};
    for (const record of selectedRecords) {
        selection[record.id] = record;
    }

    const handleRowSelection = (row: { id: string }) => {
        if (!setSelectedRecords) return;
        if (selection[row.id]) {
            setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
        } else {
            const fullRecord = visibleItems.find(item => item.id === row.id);
            if (fullRecord) {
                setSelectedRecords([...selectedRecords, fullRecord]);
            }
        }
    };

    const handleSelectAllRows = () => {
        if (!setSelectedRecords) return;
        if (selectedRecords.length === visibleItems.length) {
            setSelectedRecords([]);
        } else {
            setSelectedRecords([...visibleItems]);
        }
    };

    const handleRecordClick = (record: PaymentIntent) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: PaymentIntent) => {
        if (selectedRecord?.id) {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.automaticPaymentMethods;
            delete payload.clientSecret;
            await update(selectedRecord.id, payload as PaymentIntent);
            queryClient.invalidateQueries({queryKey: ['paymentIntents']});
        }
    };

    const tableColumns = getColumns(handleRecordClick);

    const getColumnsWithSelection = () => {
        let cols: IPaymentIntentTableColumn[] = [];
        if (allowedActions.includes(rbacActions.DeletePaymentIntent)) {

            cols.push({
                          id: 'select',
                          title: '',
                          render: item => {
                              return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                          },
                      },
            );
        }

        cols = cols.concat(tableColumns);

        return cols;

    };


    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={10}
                handleItemsPerPageChange={() => {}}
                handleFilterByKeyword={handleFilterByKeyword}
                settings={settings}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumnsWithSelection() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={visibleItems}
                    loading={loading}
                    selection={selection}
                    columns={getColumnsWithSelection() as TableBodyColumn[]}
                />
            </Table>
            <PaginationControls
                currentPage={currentPage}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onNext={goToNextPage}
                onPrevious={goToPreviousPage}
                loading={loading}
            />

            <FormDrawer
                record={selectedRecord || {} as PaymentIntent}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
